import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { XEMEX_OID } from '@core/constants';
import { canViewInstallations } from '@core/helpers/installation-access.helper';
import { Installation, getUserFeaturePermissions } from '@core/models';
import { AuthStore } from '@root/app/store/auth.store';
import { InstallationAttributesStore } from '@root/app/store/installation-attributes.store';
import { UserStore } from '@root/app/store/user.store';
import { fedDevices } from '../../fed-devices';
import { InstallationAlarmSettingsCardComponent } from '../installation-alarm-settings-card/installation-alarm-settings-card.component';
import { InstallationPvForecastCardComponent } from '../installation-pv-forecast-card/installation-pv-forecast-card.component';

@Component({
  selector: 'xemex-installation-settings-tab',
  imports: [InstallationAlarmSettingsCardComponent, InstallationPvForecastCardComponent],
  // Here rather than on the page, so the reads run only while this tab is shown.
  providers: [InstallationAttributesStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-settings-tab.component.html',
})
export class InstallationSettingsTabComponent {
  installation = input.required<Installation>();

  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly attributes = inject(InstallationAttributesStore);

  readonly feeding = computed(() => fedDevices(this.installation()));

  // The attributes are read from the device and its forecast config, so a role that may not see
  // devices is not shown them here either.
  readonly showAttributes = computed(() => {
    const user = this.userStore.user();
    const permissions = this.authStore.permissions();
    return (
      canViewInstallations(user, permissions) &&
      getUserFeaturePermissions(user, permissions, XEMEX_OID).includes('view_device')
    );
  });

  constructor() {
    // The PV forecast blocks come from these reads.
    this.attributes.load(
      computed(() => (this.showAttributes() ? this.feeding().map(device => device.deviceId) : [])),
    );
  }
}
